import { cn } from '@/lib/utils';
import { CircleAlert } from 'lucide-react';
import * as React from 'react';

type TextAreaRootProps = React.HtmlHTMLAttributes<HTMLDivElement>;

function TextAreaWrapper({ className, ...props }: TextAreaRootProps) {
	return (
		<div
			className={cn(
				'focus-visible:ring-ring flex min-h-20 w-full items-start gap-2 rounded-md border border-zinc-200 bg-inherit px-3 py-2 text-sm ring-offset-0 transition-all duration-150 placeholder:text-zinc-500 focus-within:ring-1 focus-within:ring-cyan-800 focus-within:ring-offset-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
				className,
			)}
			{...props}
		/>
	);
}

export type TextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
	({ className, ...props }, ref) => {
		return (
			<textarea
				className={cn(
					'min-h-16 w-full flex-1 resize-none border-0 bg-transparent p-0 text-dark outline-none placeholder:text-sm placeholder:text-zinc-500 focus:outline-none',
					className,
				)}
				ref={ref}
				{...props}
			/>
		);
	},
);
TextArea.displayName = 'TextArea';

type TextAreaLabelProps = {
	text?: string;
	className?: string;
	icon?: React.ReactNode;
} & React.LabelHTMLAttributes<HTMLLabelElement>;

const TextAreaLabel = ({
	text,
	className,
	icon,
	...props
}: TextAreaLabelProps) => {
	return (
		<div className="flex items-center gap-1">
			{icon && icon}
			<label
				className={cn('text-sm font-medium text-dark', className)}
				{...props}
			>
				{text}
			</label>
		</div>
	);
};

type TextAreaErrorProps = {
	error: string;
	className?: string;
};

const TextAreaError = ({ error, className }: TextAreaErrorProps) => {
	return (
		<div className="flex items-center gap-1 pt-2">
			<CircleAlert className="h-3 w-3 text-red-500" />
			<p className={cn('text-xs font-semibold text-red-500', className)}>
				{error}
			</p>
		</div>
	);
};

export { TextArea, TextAreaWrapper, TextAreaLabel, TextAreaError };
